import { useCallback, useEffect, useRef, useState } from 'react';

import {
  canViewInsightSurface,
  type InsightSurface,
} from '../config/insightAccess';
import { useAuth } from '../contexts/AuthContext';
import { useHouseholdMembership } from '../contexts/HouseholdMembershipContext';
import {
  listInsights,
  type InsightCard,
} from '../services/assistantService';
import { subscribeDashboardRefresh } from '../services/dashboardRefreshBus';
import {
  ensureInsightsGeneratedOnce,
  insightRefreshKey,
  invalidateInsightRefresh,
} from '../services/insightRefreshGate';

/** Matches budget-server `InsightWidgetTarget`. */
export type InsightWidgetTarget = InsightSurface;

type Options = {
  /** When false, skips fetch (e.g. modal closed). Default true. */
  enabled?: boolean;
};

function matchesWidget(card: InsightCard, widget: InsightWidgetTarget): boolean {
  if (card.primaryWidget === widget) return true;
  const targets = card.widgetTargets;
  if (Array.isArray(targets) && targets.includes(widget)) return true;
  // Older cards / generic overview — still useful on home spend & trends.
  if (
    (widget === 'home_spend' || widget === 'spend_trends') &&
    (!targets?.length || targets.includes('home_strip'))
  ) {
    return true;
  }
  if (widget === 'wealth_trends' && card.type === 'retirement_gap') return true;
  return false;
}

/**
 * Precomputed InsightCards for a widget surface (`GET /insights?view=&widget=`).
 * Falls back to unfiltered list + client match so callouts still show when
 * server-side widgetTargets are missing on older cards.
 */
export function useWidgetInsights(
  widgetTarget: InsightWidgetTarget,
  options?: Options,
): {
  insights: InsightCard[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  primary: InsightCard | null;
} {
  const { isAuthenticated, user } = useAuth();
  const { view, viewReady } = useHouseholdMembership();
  const enabled = options?.enabled !== false;
  const isPro = user?.plan === 'pro';
  const canView = canViewInsightSurface(widgetTarget, isPro);
  const requestIdRef = useRef(0);

  const [insights, setInsights] = useState<InsightCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !canView || !enabled || !viewReady) {
      if (!isAuthenticated || !canView || !enabled) setInsights([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      let res = await listInsights(view, { widget: widgetTarget });
      let cards = res.insights ?? [];

      if (cards.length === 0) {
        const all = await listInsights(view);
        cards = (all.insights ?? []).filter((c) => matchesWidget(c, widgetTarget));
      }

      if (cards.length === 0) {
        await ensureInsightsGeneratedOnce(insightRefreshKey(user?.id, view));
        if (requestId !== requestIdRef.current) return;
        res = await listInsights(view, { widget: widgetTarget });
        cards = res.insights ?? [];
        if (cards.length === 0) {
          const all = await listInsights(view);
          cards = (all.insights ?? []).filter((c) => matchesWidget(c, widgetTarget));
          if (cards.length === 0 && widgetTarget === 'home_spend') {
            cards = (all.insights ?? []).slice(0, 2);
          }
        }
      }

      if (requestId !== requestIdRef.current) return;
      setInsights(cards);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setInsights([]);
      setError(err instanceof Error ? err.message : 'Could not load insights');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [canView, enabled, isAuthenticated, view, viewReady, widgetTarget, user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeDashboardRefresh(() => {
      invalidateInsightRefresh('widget_insights');
      void refresh();
    });
  }, [refresh]);

  return {
    insights,
    loading,
    error,
    refresh,
    primary: insights[0] ?? null,
  };
}
